<template>
  <div data-test="usmediaslider" class="glide us-media-slider" tabindex="0">
    <div class="glide__track" data-glide-el="track">
      <div class="glide__slides">
        <slot />
      </div>
    </div>
    <div data-glide-el="controls" class="us-media-slider__controls">
      <USArrowButton
        direction="left"
        data-glide-dir="<"
        fill="var(--color--neutral-a5)"
      />
      <USArrowButton
        direction="right"
        data-glide-dir=">"
        fill="var(--color--neutral-a5)"
      />
    </div>
  </div>
</template>
<script lang="ts">
import { defineComponent } from 'vue'

import USArrowButton from '../USArrowButton/USArrowButton.vue'

import Glide from '@glidejs/glide'
import { GlideKeyboard } from '@base/@motiv/fe-patterns'

export default defineComponent({
  name: 'USMediaSlider',
  components: {
    USArrowButton
  },
  data: (): {
    glide: any
    glideKeyboard: GlideKeyboard | null
  } => ({
    glide: null,
    glideKeyboard: null
  }),
  mounted() {
    this.glide = new Glide(this.$el, {
      autoplay: 10000,
      type: 'carousel',
      keyboard: false,
      focusAt: 'center',
      perView: 1,
      gap: 20,
      peek: 600,
      breakpoints: {
        2150: {
          peek: 500
        },
        1800: {
          peek: 400
        },
        1600: {
          peek: 300
        },
        1400: {
          peek: 200
        },
        1080: {
          peek: 120
        },
        768: {
          peek: 40,
          gap: 10
        }
      }
    })

    const slides: NodeListOf<HTMLElement> = this.$el.querySelectorAll(
      '.glide__slide:not(.slide__slide--clone)'
    )

    interface lazySlide {
      imgEl: HTMLElement
      index: number
      handled: boolean
    }

    const lazySlides: lazySlide[] = []

    slides.forEach((slide, i) => {
      const img = slide.querySelector('img')

      if (img && img.getAttribute('data-src')) {
        lazySlides.push({
          imgEl: img,
          index: i,
          handled: false
        })
      }
    })

    this.glide.on(['run.before', 'mount.before'], (e) => {
      let index = this.glide.index

      if (e.direction == '>') index++
      else if (e.direction == '<') index--

      lazySlides.forEach((slide: lazySlide) => {
        if (slide.handled) return

        if (
          slide.index - index <= 2 ||
          slide.index - index >= slides.length - 2
        ) {
          const src = slide.imgEl.getAttribute('data-src')
          if (src) slide.imgEl.setAttribute('src', src)

          const srcset = slide.imgEl.getAttribute('data-srcset')
          if (srcset) slide.imgEl.setAttribute('srcset', srcset)

          slide.handled = true
        }
      })
    })

    this.glide.mount()

    this.glideKeyboard = new GlideKeyboard('.glide.us-media-slider', this.glide)
  },
  unMounted() {
    if (this.glide) {
      this.glide.destroy()
    }

    if (this.glideKeyboard?.destroy()) {
      this.glideKeyboard.destroy()
    }
  }
})
</script>

<style lang="scss" scoped>
@use '@base/styles/v1.0/scss/foundations/breakpoint' as bp;
// @import 'node_modules/@glidejs/glide/src/assets/sass/glide.core';

.us-media-slider {
  position: relative;

  &:hover {
    .us-media-slider__controls {
      opacity: 1;
    }
  }

  &__controls {
    align-items: center;
    height: 100%;
    display: none;
    height: 100%;
    justify-content: space-between;
    max-width: bp.$breakpoint--desktop;
    margin: 0 auto;
    opacity: 0;
    pointer-events: none;
    padding: var(--spacing--6);
    position: absolute;
    left: 0;
    right: 0;
    transition: opacity var(--animation-speed-medium) ease;
    top: 0;
    width: 100%;

    @media (min-width: bp.$breakpoint--tablet) {
      display: flex;
    }

    button {
      pointer-events: all;
    }
  }
}
</style>
